import { DataType, Form, FormOptions, Section, SelectOption } from '@globaleris/ui.library'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  EnumClaseFactura,
  EnumEstadoCiclo,
  EnumEstadoFactura,
  EnumEstadoFichero,
  EnumTipoDocumento
} from '../../../../api'
import { EntityForDomain } from '../../../../api/Argentia/endpoints/tasks/model/tasks'
import { ProcedureStates, ProcedureTypes } from '../../../../api/Argentia/enums'
import { EnumDominioTarea } from '../../../../api/oData/enums/enumDominioTarea'
import { CardActionProps } from '../../../../components/shared/cardActions/cardActions'
import { StandardCard } from '../../../../components/shared/standardCard/standardCard'
import { enumToSelectOptionArray } from '../../../../helpers'
import { getCustomerDomain } from '../../../../redux'

interface TaskRelatedEntityCardProps {
  taskRelatedEntity: EntityForDomain<EnumDominioTarea> | undefined
  isLoading?: boolean
  taskDomain: EnumDominioTarea
  show?: boolean
  onViewMoreClick?: () => void
}

export const TaskRelatedEntityCard: React.FC<TaskRelatedEntityCardProps> = ({
  taskRelatedEntity,
  taskDomain,
  isLoading,
  show = true,
  onViewMoreClick
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'tasks'])

  const noData = taskRelatedEntity == null || Object.keys(taskRelatedEntity).length === 0
  const isInvoice =
    taskDomain === EnumDominioTarea.Factura || taskDomain === EnumDominioTarea.PortalClientesFactura
  const isProcedure = taskDomain === EnumDominioTarea.Tramitacion
  const isDocument = taskDomain === EnumDominioTarea.Documento
  const isLead = taskDomain === EnumDominioTarea.Lead
  const isInvoiceCycle = taskDomain === EnumDominioTarea.EjecucionCF

  // #region Handlers Starts
  // #endregion Handlers Ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  // #endregion Hooks Ends

  // #region Footer Actions Starts
  const footerActions: CardActionProps[] = [
    {
      label: t('tasks:components.taskRelatedEntityCard.actions.viewMore'),
      handleActionClick: onViewMoreClick,
      hasPermission: true,
      color: 'primary'
    }
  ]
  // #endregion Footer Actions Ends

  // setStates
  //const {errorProps}= useHttpError()
  //const { dataTableFilter, dataTableSearch } = usePageState()

  const getTypeOptions = (domain: EnumDominioTarea): SelectOption[] => {
    switch (domain) {
      case EnumDominioTarea.Documento:
        return enumToSelectOptionArray(EnumTipoDocumento, 'enums:EnumTipoDocumento')
      case EnumDominioTarea.Tramitacion:
        return enumToSelectOptionArray(ProcedureTypes, 'enums:ProcedureTypes')
      default:
        return []
    }
  }

  const getStateOptions = (domain: EnumDominioTarea): SelectOption[] => {
    switch (domain) {
      case EnumDominioTarea.Documento:
        return enumToSelectOptionArray(EnumEstadoFichero, 'enums:EnumEstadoFichero')
      case EnumDominioTarea.Factura:
        return enumToSelectOptionArray(EnumEstadoFactura, 'enums:EnumEstadoFactura')
      case EnumDominioTarea.Tramitacion:
        return enumToSelectOptionArray(ProcedureStates, 'enums:ProcedureStates')
      case EnumDominioTarea.EjecucionCF:
        return enumToSelectOptionArray(EnumEstadoCiclo, 'enums:EnumEstadoCiclo')
      default:
        return []
    }
  }

  const options: FormOptions = {
    elevation: 0,
    columns: 4,
    analytics: {
      customer: getCustomerDomain(),
      view: t('tasks:components.taskRelatedEntityCard.form.analytics')
    }
  }

  const sections: Section[] = [
    {
      title: '',
      inputs: [
        {
          name: 'id',
          display: false
        },
        {
          name: 'code',
          label: t('tasks:components.taskRelatedEntityCard.form.inputs.code'),
          type: DataType.TEXT,
          display: isProcedure || isInvoice || isLead,
          readOnly: true,
          inputSize: 6
        },
        {
          name: 'class',
          label: t('tasks:components.taskRelatedEntityCard.form.inputs.class'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumClaseFactura, 'enums:EnumClaseFactura'),
          display: isInvoice,
          readOnly: true,
          inputSize: 6
        },
        {
          name: 'type',
          label: t('tasks:components.taskRelatedEntityCard.form.inputs.type'),
          type: DataType.SELECT,
          selectOptions: getTypeOptions(taskDomain),
          display: isDocument || isProcedure,
          readOnly: true,
          inputSize: 6
        },
        {
          name: 'description',
          label: t('tasks:components.taskRelatedEntityCard.form.inputs.description'),
          type: DataType.TEXT,
          display: isDocument || isLead,
          readOnly: true,
          inputSize: 6
        },
        {
          name: 'startDate',
          label: t('tasks:components.taskRelatedEntityCard.form.inputs.startDate'),
          type: DataType.DATE,
          display: isInvoice,
          readOnly: true,
          inputSize: 6
        },
        {
          name: 'endDate',
          label: t('tasks:components.taskRelatedEntityCard.form.inputs.endDate'),
          type: DataType.DATE,
          display: isInvoice,
          readOnly: true,
          inputSize: 6
        },
        {
          name: 'emisionDate',
          label: t('tasks:components.taskRelatedEntityCard.form.inputs.emisionDate'),
          type: DataType.DATE,
          display: isInvoice,
          readOnly: true,
          inputSize: 6
        },
        {
          name: 'date',
          label: t('tasks:components.taskRelatedEntityCard.form.inputs.procedureDate'),
          type: DataType.DATE,
          display: isProcedure,
          readOnly: true,
          inputSize: 6
        },
        {
          name: 'documentDate',
          label: t('tasks:components.taskRelatedEntityCard.form.inputs.documentDate'),
          type: DataType.DATE,
          display: isDocument,
          readOnly: true,
          inputSize: 6
        },
        {
          name: 'state',
          label: t('tasks:components.taskRelatedEntityCard.form.inputs.state'),
          type: DataType.SELECT,
          selectOptions: getStateOptions(taskDomain),
          display: isDocument || isProcedure,
          readOnly: true,
          inputSize: 6
        },
        {
          name: 'status',
          label: t('tasks:components.taskRelatedEntityCard.form.inputs.status'),
          type: DataType.SELECT,
          selectOptions: getStateOptions(taskDomain),
          display: isInvoice,
          readOnly: true,
          inputSize: 6
        },
        {
          name: 'cycleStatus',
          label: t('tasks:components.taskRelatedEntityCard.form.inputs.cycleStatus'),
          type: DataType.SELECT,
          selectOptions: getStateOptions(taskDomain),
          display: isInvoiceCycle,
          readOnly: true,
          inputSize: 6
        }
      ]
    }
  ]

  return show ? (
    <StandardCard
      noTransition
      isLoading={isLoading}
      showEmptyContent={noData}
      emptyCaption={t('tasks:components.taskRelatedEntityCard.emptyCaption')}
      footerActions={footerActions}
    >
      <Form
        title={t(`tasks:components.taskRelatedEntityCard.${EnumDominioTarea[taskDomain]}.title`)}
        sections={sections}
        data={taskRelatedEntity}
        options={options}
      />
    </StandardCard>
  ) : (
    <></>
  )
}
